import { PencilLine } from 'lucide-react'

import { Avatar } from '~/components/Avatar'
import { UserAvatar } from '~/components/UserAvatar'
import { Timestamp } from '~/components/component-library/Timestamp'
import { UserDisplay } from '~/components/component-library/UserDisplay'
import { UserPopover } from '~/components/component-library/UserDisplay/components/UserPopover'
import { useSubmittedBy } from '~/components/txn-display/timeline/hooks/useSubmittedBy'
import { useAuthUser } from '~/hooks/useAuthUser'
import { BackendTransaction, OrgMember, OrgSigner } from '~/types'

const useSubmittingSignatureId = (transaction: BackendTransaction) => {
  const submittor = useSubmittedBy(transaction)
  if (!submittor) return undefined

  return transaction.signers
    .filter((s: OrgSigner) => s.user?.id === submittor.id && s.signedAt)
    .sort(
      (a, b) =>
        new Date(b.signedAt!).getTime() - new Date(a.signedAt!).getTime(),
    )[0]?.id
}

export const usePasskeySignerRows = ({
  transaction,
}: {
  transaction: BackendTransaction
}) => {
  const submittingSignatureId = useSubmittingSignatureId(transaction)
  const { user } = useAuthUser()

  if (!transaction.signers.length) return []

  return transaction.signers.map((signer: OrgSigner) => {
    const hasSignedAt = !!signer.signedAt

    if (!signer.user) {
      return {
        key: `passkey-signer-${signer.id}`,
        signedAt: signer.signedAt ?? null,
        icon: <PencilLine size={16} className="text-secondary" />,
        content: (
          <span className="flex-shrink-0 whitespace-nowrap">Signed</span>
        ),
        meta: hasSignedAt ? (
          <Timestamp
            timestamp={signer.signedAt!}
            type="relative"
            abbreviate={true}
          />
        ) : (
          <span className="whitespace-nowrap text-secondary">Unknown</span>
        ),
      }
    }

    const isSubmittor = signer.id === submittingSignatureId
    const isCurrentUser = user?.id === signer.user?.id

    return {
      key: `passkey-signer-${signer.id}`,
      signedAt: signer.signedAt ?? null,
      icon: signer.user ? (
        <UserAvatar user={signer.user} size={16} />
      ) : (
        <Avatar size={16} />
      ),
      content: (
        <UserPopover
          user={signer.user as OrgMember}
          optionalSigner={signer as OrgSigner}
          align="start"
        >
          <div className="flex min-w-0 items-center gap-1">
            <span className="flex-shrink-0 whitespace-nowrap">
              {isSubmittor ? 'Submitted' : 'Signed'} by
            </span>
            <span className="min-w-0 truncate font-medium text-primary">
              {isCurrentUser ? (
                'you'
              ) : (
                <UserDisplay
                  user={signer.user as OrgMember}
                  layout="inline-text"
                  hidePopover
                />
              )}
            </span>
          </div>
        </UserPopover>
      ),
      meta: hasSignedAt ? (
        <Timestamp
          timestamp={signer.signedAt!}
          type="relative"
          abbreviate={true}
        />
      ) : (
        <span className="whitespace-nowrap text-secondary">Unknown</span>
      ),
    }
  })
}
